import { useCallback, useMemo } from 'react';
import {
  PopoverContent,
  PopoverHeader,
  PopoverAnchor,
  PopoverBody,
  PopoverArrow,
  PopoverCloseButton,
  Popover,
  useDisclosure,
  Button,
  HStack,
  Text,
  VStack,
  Box,
} from '@chakra-ui/react';
import { useGetTopDetectionsByDetectionGroupsPerEntity } from '../../../detections/queries';
import {
  GetTopDetectionsByDetectionGroupsPerEntityFullQuery,
  Detection,
  DetectionGroups,
  DetectionCountsByMitreTactic,
} from '@investigator/shared/generated/graphql';
import { i18n } from '@investigator/shared/localization';
import { GraphQLReqStatus } from '@gamma/ui/progress';
import {
  MitreTacticsFootprint,
  MitreTactic,
  MITRE_FOOTPRINT_TACTICS,
  MITRE_TACTIC_TO_PRIORITY_LEVEL,
} from '@investigator/shared/components/MitreTacticsFootprint/MitreTacticsFootprint';
import { DetectionListSection } from './DetectionListSection';
import { SeverityScore } from '@investigator/shared/components';
import moment from 'moment';
import {
  MITRE_COLORS_PRIORITY_RANGE,
  ROUTES,
  MISSING_DATA_PLACEHOLDER,
} from '@investigator/shared/constants';
import { useEntitiesContext } from '../../context';

interface MitreTacticsPopoverProps {
  mitreTacticCounts: DetectionCountsByMitreTactic[];
  entityName: string;
  tenantEntity: string;
  tenant: string;
  start?: number;
  end?: number;
}

const { mitreTacticsPopover } = i18n.pages.entities;

const MITRE_DETECTIONS_SHOWN = 3;

interface MitreDetectionRowProps {
  detection: Detection;
  index: number;
  onClick: (e: React.MouseEvent) => void;
}

const MitreDetectionRow = ({
  detection,
  index,
  onClick,
}: MitreDetectionRowProps): JSX.Element => {
  return (
    <VStack
      key={`${detection.alert_info?.alert_name}-${index}`}
      data-testid={`mitre-detection-row-${index}`}
      py={2}
      px={2}
      width="100%"
      align="stretch"
      spacing={1}
      bg="layer.2"
      borderBottom="1px solid"
      borderColor="gray.600"
      _hover={{ bg: 'layer.3', cursor: 'pointer' }}
      _last={{ borderBottom: 'none' }}
      onClick={onClick}
    >
      <HStack width="100%">
        <HStack flex="1" minWidth={0}>
          {detection.rank?.severity !== undefined && (
            <Box flexShrink={0} mr={2}>
              <SeverityScore score={detection.rank.severity} />
            </Box>
          )}
          <Text overflow="hidden" textOverflow="ellipsis" whiteSpace="nowrap">
            {detection.alert_info?.alert_name || MISSING_DATA_PLACEHOLDER}
            <br />
            {detection.mitre_techniques?.join(', ')}
          </Text>
        </HStack>
        {detection.latest_start_timestamp && (
          <Text color="text.secondary" flexShrink={0}>
            {moment.unix(detection.latest_start_timestamp).fromNow()}
          </Text>
        )}
      </HStack>
    </VStack>
  );
};

const getGroups = (
  data: GetTopDetectionsByDetectionGroupsPerEntityFullQuery,
): Partial<Record<MitreTactic, Detection[]>> => {
  const detectionsByMitreTactic: Partial<Record<MitreTactic, Detection[]>> = {};

  data.getTopDetectionsByDetectionGroupsPerEntity?.data?.forEach(
    (mitreGroup) => {
      if (mitreGroup?.detections) {
        detectionsByMitreTactic[mitreGroup.group_key as MitreTactic] =
          mitreGroup.detections.filter((d): d is Detection => d !== null);
      }
    },
  );

  return detectionsByMitreTactic;
};

export const MitreTacticsPopover = ({
  entityName,
  tenantEntity,
  tenant,
  mitreTacticCounts,
  start,
  end,
}: MitreTacticsPopoverProps): JSX.Element => {
  const { isOpen, onToggle, onClose } = useDisclosure();
  const { analysisModal } = useEntitiesContext();

  const handleDetectionClick = useCallback(
    (detection: Detection, e: React.MouseEvent) => {
      e.stopPropagation();
      if (analysisModal.isOpen) {
        analysisModal.onClose();
      }
      // Per Mejji, this will open a new tab:
      window.open(
        ROUTES.detectionDetails(
          detection.detection_id,
          `?start=${start}&end=${end}`,
        ),
        '_blank',
      );
    },
    [start, end, analysisModal],
  );

  const { data, loading, error } =
    useGetTopDetectionsByDetectionGroupsPerEntity({
      variables: {
        tenant_entity: tenantEntity,
        group_by: DetectionGroups.MitreTactics,
        tenant: tenant,
        end: end ?? 0,
        size: MITRE_DETECTIONS_SHOWN,
        sort: null,
        start: start ?? 0,
      },
      skip: !isOpen || !start || !end || !tenantEntity || !tenant,
    });

  const groupData = useMemo(() => (data ? getGroups(data) : {}), [data]);

  return (
    <Popover
      placement="bottom-start"
      isOpen={isOpen}
      onClose={onClose}
      strategy="fixed"
      flip={true}
      modifiers={[
        {
          name: 'preventOverflow',
          options: {
            boundary: 'viewport',
            padding: 8,
          },
        },
        {
          name: 'flip',
          options: {
            fallbackPlacements: ['top-start', 'bottom-end', 'top-end'],
          },
        },
      ]}
    >
      <PopoverAnchor>
        <Button
          variant="link"
          color="text.link"
          onClick={(e) => {
            onToggle();
            e.stopPropagation();
          }}
          p={0}
          minW="auto"
          h="auto"
          borderRadius="4px"
          _hover={{
            opacity: 0.7,
            backgroundColor: 'whiteAlpha.100',
          }}
        >
          <MitreTacticsFootprint mitreTacticCounts={mitreTacticCounts} />
        </Button>
      </PopoverAnchor>
      <PopoverContent
        width="480px"
        zIndex="popover"
        onClick={(e) => e.stopPropagation()}
      >
        <PopoverArrow />
        <PopoverCloseButton w={5} h={5} fontSize="xs" right={2} top={2} />
        <PopoverHeader fontWeight="bold">
          {`${entityName} | ${mitreTacticsPopover.mitreTactics}`}
        </PopoverHeader>
        <PopoverBody maxHeight="60vh" overflowY="auto">
          {loading || error ? (
            <GraphQLReqStatus loading={loading} error={error} />
          ) : (
            <>
              {MITRE_FOOTPRINT_TACTICS.map((tactic) => {
                const detections = groupData[tactic] ?? [];
                if (!detections?.length) return null;

                return (
                  <DetectionListSection
                    key={tactic}
                    data-testid={`mitre-tactic-section-${tactic}`}
                    title={tactic}
                    detections={detections}
                    count={detections.length}
                    showButton={false}
                    circleColor={
                      MITRE_COLORS_PRIORITY_RANGE[
                        MITRE_TACTIC_TO_PRIORITY_LEVEL[tactic]
                      ]
                    }
                  >
                    {(detection, detectionIndex) => (
                      <MitreDetectionRow
                        key={detection.detection_id || detectionIndex}
                        detection={detection}
                        index={detectionIndex}
                        onClick={(e) => handleDetectionClick(detection, e)}
                      />
                    )}
                  </DetectionListSection>
                );
              })}
            </>
          )}
        </PopoverBody>
      </PopoverContent>
    </Popover>
  );
};
